import { Job, JobStatus, AdminReferral, ReferralStatus, Candidate, User } from "@/lib/schema";
import { updateJob } from "@/api/jobs";

// Extended interface for referrals with candidate info for UI display
export interface ReferralWithCandidateInfo extends AdminReferral {
  candidateName: string;
  referrerName: string;
}

// Navigation utilities
export const navigateToJobs = (navigate: (path: string) => void) => {
  navigate("/admin/jobs");
};

// User initials helper
export const getInitials = (name: string): string => {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};


// Transform referral data to include candidate and referrer information
export const enrichReferralData = (
  referral: AdminReferral, 
  candidate: Candidate | null, 
  referrer: User | null
): ReferralWithCandidateInfo => {
  const candidateName = candidate 
    ? `${candidate.firstName} ${candidate.lastName}` 
    : referral.candidateFirstName && referral.candidateLastName
    ? `${referral.candidateFirstName} ${referral.candidateLastName}`
    : 'Unknown Candidate';
    
  const referrerName = referrer 
    ? `${referrer.firstName} ${referrer.lastName}` 
    : 'Unknown Referrer';
  
  return {
    ...referral,
    candidateName,
    referrerName
  };
};

// Job status action handlers
export const getJobStatusActions = (status: JobStatus) => {
  const actions = [];
  
  if (status === JobStatus.ACTIVE) {
    actions.push({
      action: 'pause',
      label: 'Pause',
      variant: 'outline' as const,
      icon: 'Pause'
    });
  }
  
  if (status === JobStatus.PAUSED) {
    actions.push({
      action: 'activate',
      label: 'Activate',
      variant: 'outline' as const,
      icon: 'Play'
    });
  }
  
  actions.push({
    action: 'edit',
    label: 'Edit',
    variant: 'outline' as const,
    icon: 'Edit'
  });
  
  return actions;
};

// Helper function to get match score from referral
// Converts decimal scores (0-1) to percentage scores (0-100)
export const getMatchScore = (referral: ReferralWithCandidateInfo | { matchScore?: number }): number | null => {
  const score = referral.matchScore ?? null;
  if (score === null) return null;
  
  // Convert decimal to percentage if the score is between 0 and 1
  if (score >= 0 && score <= 1) {
    return Math.round(score * 100);
  }
  
  // If already a percentage (1-100), return as is
  return Math.round(score);
};


// Analytics calculations using real referral data
export const calculateJobAnalytics = (job: Job, referrals: ReferralWithCandidateInfo[] | Array<{ matchScore?: number; status: ReferralStatus }>) => {
  const totalApplications = job.applicants || referrals.length; // Use job field or fallback to referrals
  const totalReferrals = referrals.length;
  
  // Calculate average match score from referrals with match scores
  const referralsWithScores = referrals.filter(r => getMatchScore(r) !== null);
  
  const averageMatchScore = referralsWithScores.length > 0 
    ? Math.round(referralsWithScores.reduce((sum, r) => {
        const score = getMatchScore(r) || 0;
        return sum + score;
      }, 0) / referralsWithScores.length)
    : job.avgMatchScore || 0;
  
  const hiredCount = referrals.filter(r => r.status === ReferralStatus.HIRED).length;
  const conversionRate = totalReferrals > 0 
    ? Math.round((hiredCount / totalReferrals) * 100)
    : job.conversionRate || 0;
  
  return {
    totalApplications,
    totalReferrals,
    averageMatchScore,
    referralConversionRate: conversionRate,
    underReviewCount: referrals.filter(r => r.status === ReferralStatus.UNDER_REVIEW).length,
    interviewCount: referrals.filter(r => r.status === ReferralStatus.INTERVIEWED).length,
    totalAirPointsValue: job.rewardInAirPoints 
      ? totalReferrals * job.rewardInAirPoints
      : job.totalEarnings || 0,
    // Additional match score insights
    referralsWithMatchScores: referralsWithScores.length,
    highMatchScoreCount: referralsWithScores.filter(r => {
      const score = getMatchScore(r) || 0;
      return score >= 80;
    }).length,
    mediumMatchScoreCount: referralsWithScores.filter(r => {
      const score = getMatchScore(r) || 0;
      return score >= 60 && score < 80;
    }).length,
    lowMatchScoreCount: referralsWithScores.filter(r => {
      const score = getMatchScore(r) || 0;
      return score < 60;
    }).length
  };
};

// Markdown processing utilities
export const processMarkdownSection = (section: string, index: number) => {
  if (section.startsWith('# ')) {
    return {
      type: 'h1',
      content: section.replace('# ', ''),
      key: index
    };
  } else if (section.startsWith('## ')) {
    return {
      type: 'h2',
      content: section.replace('## ', ''),
      key: index
    };
  } else if (section.includes('- ')) {
    const items = section.split('\n').filter(line => line.startsWith('- '));
    return {
      type: 'list',
      items: items.map(item => item.replace('- ', '')),
      key: index
    };
  } else if (section.trim()) {
    return {
      type: 'paragraph',
      content: section,
      key: index
    };
  }
  return null;
};

export const processInlineMarkdown = (text: string): string => {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>');
};

// Copy job link utility
export const copyJobLink = (jobId: string) => {
  const link = `${window.location.origin}/jobs/${jobId}`;
  navigator.clipboard.writeText(link);
  // TODO: Add toast notification for successful copy
};

// Form validation for job updates
export const validateJobForm = (job: Partial<Job>): boolean => {
  return !!(
    job.title?.trim() &&
    job.description?.trim() &&
    job.location?.trim() &&
    job.category?.trim() &&
    job.employmentType?.trim()
  );
};

// Status update handler
export const handleJobStatusUpdate = async (jobId: string, newStatus: JobStatus | string): Promise<Job | null> => {
  try {
    const updatedJob = await updateJob(jobId, { status: newStatus as JobStatus });
    return updatedJob;
  } catch (error) {
    console.error(`Failed to update job status:`, error);
    throw error;
  }
};

// Job form state management
export const getJobFormData = (job: Job) => ({
  title: job.title,
  description: job.description,
  location: job.location,
  category: job.category,
  employmentType: job.employmentType,
  salary: job.salary || '',
  rewardInAirPoints: job.rewardInAirPoints?.toString() || '',
  status: job.status,
  priority: job.priority,
  skills: job.skills,
  assignedRecruiter: job.assignedRecruiter?.name || 'Unassigned'
});
